import React, { useEffect, useState } from 'react';
import { useSiteData } from '../context/SiteDataContext';
import { useLanguage } from '../context/LanguageContext';

const References = () => {
  const { siteData } = useSiteData();
  const { language, t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('references');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="references" className="py-16 md:py-20 bg-white pt-header">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className={`text-center mb-12 md:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('references.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-blue-600 mx-auto mb-6 md:mb-8"></div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('references.subtitle')}
          </p>
        </div>

        {/* Reference Logos - Improved mobile grid */}
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6 lg:gap-8 mb-12 md:mb-16 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {siteData.references.map((reference: any, index: number) => (
            <div
              key={reference.id}
              className="bg-white rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100 hover:border-orange-200"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="text-center">
                <div className="text-3xl md:text-4xl mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300">
                  {reference.logo}
                </div>
                <h3 className="font-bold text-gray-900 text-sm md:text-base lg:text-lg mb-1 md:mb-2 leading-tight">
                  {reference.name}
                </h3>
                <p className="text-gray-600 text-xs md:text-sm leading-relaxed">
                  {reference.sector[language]}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section - Enhanced mobile layout */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 lg:gap-8 mb-12 md:mb-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center p-4 md:p-6 lg:p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 hover:border-orange-300">
            <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-orange-600 mb-2 md:mb-3">50+</div>
            <div className="text-gray-700 font-medium text-sm md:text-base leading-tight">
              {t('references.stats.customers')}
            </div>
          </div>
          <div className="text-center p-4 md:p-6 lg:p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100 hover:border-blue-300">
            <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-600 mb-2 md:mb-3">1000+</div>
            <div className="text-gray-700 font-medium text-sm md:text-base leading-tight">
              {t('references.stats.projects')}
            </div>
          </div>
          <div className="text-center p-4 md:p-6 lg:p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-green-100 hover:border-green-300">
            <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-green-600 mb-2 md:mb-3">99%</div>
            <div className="text-gray-700 font-medium text-sm md:text-base leading-tight">
              {t('references.stats.satisfaction')}
            </div>
          </div>
          <div className="text-center p-4 md:p-6 lg:p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100 hover:border-purple-300">
            <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-purple-600 mb-2 md:mb-3">10+</div>
            <div className="text-gray-700 font-medium text-sm md:text-base leading-tight">
              {t('references.stats.experience')}
            </div>
          </div>
        </div>

        {/* Testimonial - Enhanced design */}
        <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-blue-600 rounded-3xl p-8 md:p-12 lg:p-16 text-white relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-20 h-20 bg-white rounded-full"></div>
              <div className="absolute bottom-10 right-10 w-16 h-16 bg-white rounded-full"></div>
              <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-white rounded-full"></div>
            </div>
            
            <div className="relative z-10 text-center">
              <div className="text-4xl md:text-5xl lg:text-6xl mb-4 md:mb-6">💬</div>
              <blockquote className="text-lg md:text-xl lg:text-2xl font-medium mb-4 md:mb-6 italic leading-relaxed max-w-4xl mx-auto">
                {t('references.testimonial.quote')}
              </blockquote>
              <div className="font-bold text-lg md:text-xl lg:text-2xl">
                {t('references.testimonial.author')}
              </div>
              <div className="text-orange-200 text-sm md:text-base mt-2">
                {t('references.testimonial.position')}
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className={`mt-12 md:mt-16 text-center transition-all duration-1000 delay-900 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-gray-50 rounded-2xl p-6 md:p-8 lg:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t('references.cta.title')}
            </h3>
            <p className="text-lg md:text-xl text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('references.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+905372959612"
                className="bg-orange-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-orange-600 transition-colors shadow-lg hover:shadow-xl flex items-center justify-center min-h-[56px] focus:outline-none focus:ring-4 focus:ring-orange-500/50"
              >
                <span className="text-xl mr-2">📞</span>
                {t('references.cta.call')}
              </a>
              <button
                onClick={() => {
                  const message = encodeURIComponent(t('whatsapp.reference'));
                  window.open(`https://wa.me/905372959612?text=${message}`, '_blank');
                }}
                className="bg-green-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-green-600 transition-colors shadow-lg hover:shadow-xl flex items-center justify-center min-h-[56px] focus:outline-none focus:ring-4 focus:ring-green-500/50"
              >
                <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default References;
